import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { NgbModal } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, Subscription } from 'rxjs';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import { AuthService } from '../../../auth/auth.service';
import { CoachingType, GoalStatus } from '../../../coaching-base/general';
import { OpportunityType } from '../../../coaching-base/opportunity';
import { CmpsDatePipe } from '../../../common/date.pipe';
import { CmpsDecimalPipe } from '../../../common/number.pipe';
import { CacheDateItem } from '../../../performance-data/cache-date.pd.model';
import { PerformanceDataViewType } from '../../../performance-data/metric.pd.model';
import { PerformanceDataService } from '../../../performance-data/performance-data.service';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import {
    addDays,
    CoachingDateToday,
    CoachingInsightComponentAttributes,
    endOfWeek,
    getQuarter,
    prependEmptyOption,
} from '../models/coaching.global.model';
import {
    ACTIVE_TAB_VIEW,
    COACHING_PERFORMANCE_PERIOD_TYPES,
    CommitmentListEntry,
    ElasticTabConfig,
    GraphType,
    Metric,
    MetricDetailGridObservables,
    OaPerson,
    Opportunity,
    PerformanceData,
    RawGraphData,
} from '../models/oa.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';
import { TeamOAService } from '../services/team-oa.service';

const colors = [
    'red',
    'blue',
    'green',
    'purple',
    'maroon',
    'navy',
    'olive',
    'orange',
    'teal',
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-unused-vars
function generateColor() {
    return colors[Math.floor(Math.random() * colors.length)];
}

@Component({
    selector: '[personRowDetail]',
    templateUrl: './team-oa-row-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamOARowDetailsComponent implements OnInit, OnDestroy {
    @ViewChild('perfContent') perfContent: ElementRef;

    @Input() member: OaPerson;
    @Input() activeMetric: Opportunity;
    @Input() activecolumns: Observable<number>;
    @Input() activeView: ACTIVE_TAB_VIEW;
    @Input() openNonKPI: boolean;
    @Output() loaded: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    @Output() activeMetricChange: EventEmitter<Opportunity> =
        new EventEmitter<Opportunity>();

    @Output() triggerDone: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('startObservationPopup') ya: TemplateRef<any>;

    @HostBinding('class') classes = 'details-container row-expanded';

    activeMetricCode: string;

    COACHING_PERFORMANCE_PERIOD_TYPES = COACHING_PERFORMANCE_PERIOD_TYPES;
    PeriodType = PeriodType;
    GraphType = GraphType;

    activeSubTab: GraphType = GraphType.VARIANCE;

    metrics: Observable<Metric[]>;

    metricname: string;

    i18n = TranslationKeyMap;

    globalPeriodType: Observable<string>;
    localPeriodType: PeriodType;
    daterange: PeriodType;
    extradata: Subscription;

    showUncoachedFlag = false;
    showCommitmentHistory = false;

    selectedComponents: any[] = [];
    componentsOptions: IOption[];
    metricComponents: any[];
    showPrimaryMetricGraph: boolean;

    private _componentAttributes: CoachingInsightComponentAttributes;
    private readonly resizesub: Subscription;

    actualGridData: PerformanceData[] = null;
    componentLength: number;
    gridPeriods: any[] = null;
    metricnames: string[] = [];

    actualVarianceData: RawGraphData[];
    actualTrendData: RawGraphData[];
    componentData: RawGraphData[];

    startInitVariance = false;
    startInitTrend = false;
    startInitGrid = false;

    hasPendingData = false;

    perfTabsConfig = [
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {
            text: GraphType.VARIANCE,
            activeConditionComparator: GraphType.VARIANCE,
            clickHandler: this.selectActiveSubTab.bind(this),
            clickHandlerParams: [GraphType.VARIANCE],
        } as ElasticTabConfig,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {
            text: GraphType.TREND,
            activeConditionComparator: GraphType.TREND,
            clickHandler: this.selectActiveSubTab.bind(this),
            clickHandlerParams: [GraphType.TREND],
        } as ElasticTabConfig,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {
            text: this._translateService.instant(TranslationKeyMap._555_DETAIL),
            activeConditionComparator: GraphType.GRID,
            clickHandler: this.selectActiveSubTab.bind(this),
            clickHandlerParams: [GraphType.GRID],
        } as ElasticTabConfig,
    ];

    metricOptions: any[];
    loadedTrend: boolean;
    oppTypeLabel: string = '';

    constructor(
        private readonly _coachingInsightService: CoachingInsightWidgetService,
        private readonly _teamOAService: TeamOAService,
        private readonly _coachingService: CoachingService,
        private readonly _authUserService: AuthService,
        private readonly _translateService: TranslateService,
        private readonly _decimalPipe: CmpsDecimalPipe,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _modalService: NgbModal,
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        private readonly cd: ChangeDetectorRef,
        private readonly _piService: PerformanceDataService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        // eslint-disable-next-line no-return-assign
        COACHING_PERFORMANCE_PERIOD_TYPES.forEach(
            (value) =>
                (value.label = this._translateService.instant(value.tlKey)),
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get user() {
        return this._authUserService.user;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get element() {
        return this._el.nativeElement;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this._componentAttributes =
            this._coachingInsightService.getComponentAttributes(0); // componentTypeId = 0

        if (
            !this.activeMetric ||
            (this.activeMetric && !this.activeMetric.metricId)
        ) {
            this.activeSubTab = GraphType.TREND;
            setTimeout(() => {
                this._teamOAService.triggerMemberData(this.member);
                this._emitDone(true);
                // eslint-disable-next-line @typescript-eslint/dot-notation
                if (!this.cd['destroyed']) {
                    this.cd.detectChanges();
                }
            }, 100);
        } else {
            const componentswithgoals = this.activeMetric.components.filter(
                (c: Opportunity) => {
                    return (
                        c.opportunityType === OpportunityType.ACTIVE ||
                        c.opportunityType === OpportunityType.CLOSED_NOT_OPP ||
                        c.opportunityType === OpportunityType.CLOSED_STILL_OPP
                    );
                },
            );
            this.activeSubTab =
                (this.activeMetric.opportunityType ===
                    OpportunityType.NO_GOAL ||
                    this.activeMetric.opportunityType ===
                        OpportunityType.PENDING) &&
                !componentswithgoals.length
                    ? GraphType.VARIANCE
                    : GraphType.TREND;
            this.onActiveMetricSelected(this.activeMetric.metricId);
        }

        this._teamOAService.resetComponentData();

        this._teamOAService.initMember(this.member.userId);

        this.metrics = this._teamOAService.metrics;
        this.globalPeriodType = this._coachingInsightService.periodTypeId;

        this.localPeriodType = PeriodType.DAY;
        this.daterange = this._coachingInsightService.getPeriodTypeEnumId();

        this.metricOptions = [
            ...this.member.coached,
            ...this.member.uncoached,
            ...this.member.nonoa,
            ...this.member.missed,
        ];

        prependEmptyOption(
            this.metricOptions,
            this._translateService.instant(
                TranslationKeyMap._7401_SELECT_METRIC_,
            ),
        );

        this.oppTypeLabel = this._translateService.instant(
            TranslationKeyMap._7397_PLEASE_SELECT_A_METRIC_BELOW,
        );

        this.updateOppTypeLabel();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('periodType')) {
            this._initchart();
        }
    }

    updateOppTypeLabel(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.activeMetric && this.activeMetric.metricId) {
            this.oppTypeLabel = `${this.activeMetric.metric.metricName} (`;
            switch (this.activeMetric.coachingType) {
                case CoachingType.COACHED:
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    this.oppTypeLabel += this._translateService.instant(
                        TranslationKeyMap._7394_COACHED_OA,
                    );
                    break;
                case CoachingType.UNCOACHED:
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    this.oppTypeLabel += this._translateService.instant(
                        TranslationKeyMap._7395_UNCOACHED_OA,
                    );
                    break;
                case CoachingType.NON_OA:
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    this.oppTypeLabel += this._translateService.instant(
                        TranslationKeyMap._7396_NON_OA,
                    );
                    break;
                case CoachingType.MISSED:
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    this.oppTypeLabel += this._translateService.instant(
                        TranslationKeyMap._7508_MISSED_OPPORTUNITIES,
                    );
                    break;
            }
            this.oppTypeLabel += ')';
        }
    }

    emptyObjectProps(obj): RawGraphData {
        const newobj: RawGraphData = {};
        for (const key in obj) {
            newobj[key] = null;
        }
        return newobj;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getMetric(id: any) {
        if (id === null) return;

        const x: Opportunity = this.member.opportunities
            ? this.member.opportunities.find((e) => e.metricId === parseInt(id))
            : null;

        this._teamOAService.setActiveMetric(x);

        if (x.components) {
            this.componentsOptions = [];
            x.components.forEach((c) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                this.componentsOptions.push({
                    value: c.metric.metricId.toString(),
                    label: c.metric.metricName,
                } as IOption);
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _initchart() {
        if (!this.activeMetric || !this.activeMetric.metricId) return;

        if (this.extradata) {
            this.extradata.unsubscribe();
            this.extradata = null;
        }

        this.componentData = [];

        switch (this.activeSubTab) {
            case GraphType.TREND:
                if (this.startInitTrend) return;

                this.selectedComponents = [];
                this.actualTrendData = [];

                this.generateGraphDataRequest(CoachingDateToday, true);

                break;
            case GraphType.VARIANCE:
                if (this.startInitVariance) return;

                this.actualVarianceData = [];

                this.generateGraphDataRequest(CoachingDateToday, true);

                break;
            case GraphType.GRID:
                if (this.startInitGrid) return;
                this._resetGrid();

                break;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _resetGrid() {
        this.actualGridData = null;

        this.hasPendingData = true;

        this._piService
            .listCacheDates(PeriodType.MONTH)
            .subscribe((g: CacheDateItem[]) => {
                this.hasPendingData = false;

                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                const topmtd = g && g.length ? g[0] : null;
                const topdate = topmtd ? topmtd.endDate : CoachingDateToday;
                this.generateGridDataRequest(topdate);
            });

        // const sub = this._piService
        // .listCacheDates(PeriodType.MONTH)
        //     .subscribe((g: Array<CacheDateItem>) => {
        //         const topmtd = g && g.length ? g[0] : null;
        //         if (this.daterange === PeriodType.MONTH) {
        //             const topdate = topmtd ? topmtd.eventDate : CoachingDateToday;
        //             this.generateGridDataRequest(topdate);
        //         } else {
        //             this.generateGridDataRequest(CoachingDateToday);
        //         }

        //         if (sub) {
        //             sub.unsubscribe();
        //         }
        // });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    set30DayDates(today) {
        return [
            {
                endDate: today,
                startDate: addDays(today, -30),
            },
            {
                endDate: addDays(today, -31),
                startDate: addDays(today, -60),
            },
            {
                endDate: addDays(today, -61),
                startDate: addDays(today, -90),
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setMonthDates(today) {
        const month = today.getMonth();
        const year = today.getFullYear();

        return [
            {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                endDate: new Date(year, month + 1, 0),
                startDate: new Date(year, month, 1),
            }, // end date of this month
            {
                endDate: new Date(year, month, 0),
                startDate: new Date(year, month - 1, 1),
            }, // end date of last month
            {
                endDate: new Date(year, month - 1, 0),
                startDate: new Date(year, month - 2, 1),
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setWeekDates(today) {
        const eow = endOfWeek(today);

        return [
            {
                endDate: eow,
                startDate: addDays(eow, -6),
            },
            {
                endDate: addDays(eow, -7),
                startDate: addDays(eow, -13),
            },
            {
                endDate: addDays(eow, -14),
                startDate: addDays(eow, -20),
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setQuarterDates(today) {
        const thisQuarter = getQuarter(today);
        const prevQuarter = getQuarter(addDays(thisQuarter.StartDate, -1));
        const prevprevQuarter = getQuarter(addDays(prevQuarter.StartDate, -1));

        return [
            {
                endDate: thisQuarter.EndDate,
                startDate: thisQuarter.StartDate,
            },
            {
                endDate: prevQuarter.EndDate,
                startDate: prevQuarter.StartDate,
            },
            {
                endDate: prevprevQuarter.EndDate,
                startDate: prevprevQuarter.StartDate,
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    generateGridDataRequest(today) {
        const dates = [];
        today = new Date(today);

        switch (this.daterange) {
            case PeriodType.ROLLING_30:
                dates.push(...this.set30DayDates(today));
                break;

            case PeriodType.MONTH:
                dates.push(...this.setMonthDates(today));
                break;

            case PeriodType.WEEK:
                dates.push(...this.setWeekDates(today));
                break;

            default:
                dates.push(...this.setQuarterDates(today));
                break;
        }

        this.gridPeriods = dates;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const tempdata = [];

        this.metricnames = [];

        const periodicData: MetricDetailGridObservables[] = [];

        dates.forEach((date, idx) => {
            const opts = {
                viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                periodType: this.daterange,
                date: date.endDate, // get the end date for each period
                userId: this.member.userId,
                metricId: [this.activeMetric.metricId],
            };

            const metricrequests = [];
            this.metricnames.push(this.activeMetric.metric.metricShortName);
            metricrequests.push(this.pullGridData(opts));

            const components = this.activeMetric.coachedComponents;
            this.componentLength = 0;

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (components && components.length) {
                components.forEach((c) => {
                    if (
                        c.opportunityType === OpportunityType.ACTIVE ||
                        c.opportunityType ===
                            OpportunityType.CLOSED_STILL_OPP ||
                        c.opportunityType === OpportunityType.CLOSED_NOT_OPP
                    ) {
                        opts.metricId = [c.metricId];
                        this.componentLength++;
                        this.metricnames.push(c.metric.metricShortName);
                        metricrequests.push(this.pullGridData(opts));
                    }
                });
            }

            periodicData.push({
                period: date,
                observables: metricrequests,
            });
        });

        this.actualGridData = [];
        this.startInitGrid = true;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const cursor = 0;

        const periodicObservables: Array<Observable<PerformanceData[]>> = [];

        periodicData.forEach((p) => {
            periodicObservables.push(...p.observables);
        });

        if (periodicData.length && periodicData[0].observables) {
            this.getPeriods(periodicObservables);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getPeriods(periodicObservables) {
        const sub = forkJoin(periodicObservables).subscribe(
            (d) => {
                if (!d) return;

                if (sub) {
                    sub.unsubscribe();
                }

                const periodPerf: PerformanceData[] = [];

                d.forEach((e: PerformanceData[]) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (e && e.length) {
                        const f = e.length - 1;
                        periodPerf.push(e[f]); // get last
                    } else {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        periodPerf.push({} as PerformanceData);
                    }
                });

                this.actualGridData.push(...periodPerf);

                this.hasPendingData = false;
                // eslint-disable-next-line @typescript-eslint/dot-notation
                if (!this.cd['destroyed']) {
                    this.cd.detectChanges();
                }
            },
            () => {
                this.startInitGrid = true;
                this.actualGridData = null;
                this.hasPendingData = false;

                // eslint-disable-next-line @typescript-eslint/dot-notation
                if (!this.cd['destroyed']) {
                    this.cd.detectChanges();
                }
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    generateGraphDataRequest(today, withSGAvg = true, component?: Opportunity) {
        const lastyear = addDays(today, -90);
        const metricId = component
            ? component.metricId
            : this.activeMetric.metricId;

        if (!today || !lastyear) return;

        const opts = {
            viewType: PerformanceDataViewType.USER_SUPER_GROUP,
            periodType: this.localPeriodType, // this.localPeriodType
            date: today, // get the end date for each period
            startDate: lastyear,
            userId: this.member.userId,
            metricId: [metricId],
        };

        if (this.activeSubTab === GraphType.TREND) {
            this.startInitVariance = false;
        } else if (this.activeSubTab === GraphType.VARIANCE) {
            this.startInitTrend = false;
        }

        const req = this.pullGraphData(opts, component ? true : null);
        if (withSGAvg) {
            const opts2 = {
                ...opts,
                viewType: PerformanceDataViewType.SUPER_GROUP,
            };
            const req2 = this.pullGraphData(opts2, component ? true : null);

            this.graphRequest([req, req2], component);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    selectActiveSubTab(name: GraphType) {
        this.activeSubTab = name;
        this.onActiveMetricSelected(this.activeMetric.metricId);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    pullGraphData(opts: any, hideSpinner = null) {
        if (!hideSpinner) {
            this.hasPendingData = true;
        }

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }

        if (opts.periodType === PeriodType.ROLLING_30) {
            return this._teamOAService.retrieveCoachingPerformanceData(
                this.activeSubTab,
                opts,
            );
        } else {
            return this._teamOAService.retrievePerformanceData(
                this.activeSubTab,
                opts,
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    graphRequest(requests, component) {
        const sub = forkJoin(requests).subscribe(
            (data: any) => {
                if (!data) return;

                if (this.activeSubTab === GraphType.TREND) {
                    this.startInitTrend = true;

                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const newdata = {
                        mainData: data[0],
                        sgAvgData: data[1],
                        showSg: false,
                        component: component || undefined,
                    } as RawGraphData;

                    this.actualTrendData.forEach((f) => {
                        f.showSg = false;
                        return f;
                    });

                    this.actualTrendData = [...this.actualTrendData, newdata];
                } else {
                    this.startInitVariance = true;
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const newdata = {
                        mainData: data[0],
                        sgAvgData: data[1],
                        showSg: false,
                    } as RawGraphData;
                    this.actualVarianceData = [
                        ...this.actualVarianceData,
                        newdata,
                    ];
                }
            },
            () => {
                // error
                if (this.activeSubTab === GraphType.TREND) {
                    this.actualTrendData = [];
                }
                if (this.activeSubTab === GraphType.VARIANCE) {
                    this.actualVarianceData = [];
                }
            },
            () => {
                // complete
                this.hasPendingData = false;
                if (sub) sub.unsubscribe();
                // eslint-disable-next-line @typescript-eslint/dot-notation
                if (!this.cd['destroyed']) {
                    this.cd.detectChanges();
                }
            },
        );
    }

    pullGridData(opts): Observable<any> {
        this.hasPendingData = true;

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }

        return this._teamOAService.retrievePerformanceData(
            this.activeSubTab,
            opts,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onPeriodTypeClick(e) {
        if (!this.activeMetric || !this.activeMetric.metricId) return;

        this.localPeriodType = e;

        if (this.activeSubTab === GraphType.TREND) {
            this.startInitTrend = false;
        } else if (this.activeSubTab === GraphType.VARIANCE) {
            this.startInitVariance = false;
        }

        this._initchart();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onDateRangeChange(e) {
        if (!this.activeMetric || !this.activeMetric.metricId) return;

        this.daterange = e;

        if (this.activeSubTab === GraphType.GRID) {
            this.startInitGrid = false;
        }

        this._initchart();
    }

    onComponentSelected(event): void {
        if (event && this.activeMetric) {
            const componentSelected = this.activeMetric.components.filter(
                (d) => d.metricId.toString() === event.value,
            );
            if (componentSelected.length) {
                // ----------------------------------------------------append to trend data
                this.generateGraphDataRequest(
                    CoachingDateToday,
                    true,
                    componentSelected[0],
                );
            }
        }
    }

    onAllComponentSelected(event): void {
        this.selectedComponents = [];
        this.actualTrendData = [this.actualTrendData[0]];

        this.activeMetric.components.forEach((el, idx) => {
            this.selectedComponents.push(el.metricId.toString());
            this.generateGraphDataRequest(CoachingDateToday, true, el);
        });

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }
    }

    onComponentDeselected(event): void {
        const idx = this.selectedComponents.indexOf(event.value);
        const newarray = [...this.selectedComponents];
        newarray.splice(idx, 1);
        this.selectedComponents = newarray;

        this._ngZone.runOutsideAngular(() => {
            this._ngZone.run(() => {
                setTimeout(() => {
                    const componentdeselected =
                        this.activeMetric.components.filter(
                            (d) => d.metricId.toString() === event.value,
                        );
                    if (componentdeselected.length) {
                        this.actualTrendData = this.actualTrendData.filter(
                            (f) => {
                                f.showSg = false;
                                return (
                                    !f.component ||
                                    f.component.metricId.toString() !==
                                        componentdeselected[0].metricId.toString()
                                );
                            },
                        );
                    }
                }, 100);
            });
        });

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }
    }

    onAllComponentDeselected(event): void {
        this.selectedComponents = [];
        // retain only the first RawGraphData, which is the active Metric
        const primarymetric = this.actualTrendData[0];
        primarymetric.showSg = false;
        this.actualTrendData = [primarymetric];

        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cd['destroyed']) {
            this.cd.detectChanges();
        }
    }

    onActiveMetricSelected(event): void {
        this.selectedComponents = [];

        this._teamOAService.setActiveMetric(null);

        if (this.activeSubTab === GraphType.TREND) {
            this.startInitTrend = false;
        }
        if (this.activeSubTab === GraphType.VARIANCE) {
            this.startInitVariance = false;
        }
        if (this.activeSubTab === GraphType.GRID) {
            this.startInitGrid = false;
        }

        if (event) {
            this.getMetric(event);

            // eslint-disable-next-line @typescript-eslint/dot-notation
            if (!this.cd['destroyed']) {
                this.cd.detectChanges();
            }

            setTimeout(() => {
                this._teamOAService.triggerMemberData(this.member);
                this._emitDone(true);
                this.updateOppTypeLabel();
                this.activeMetricChange.emit(this.activeMetric);
                this._initchart();

                // auto-plot all component metrics that are coached
                this.plotComponents();
            });
        } else {
            this.startInitTrend =
                this.startInitVariance =
                this.startInitGrid =
                    false;

            // eslint-disable-next-line @typescript-eslint/dot-notation
            if (!this.cd['destroyed']) {
                this.cd.detectChanges();
            }

            setTimeout(() => {
                this._teamOAService.triggerMemberData(this.member);
                this._emitDone(true);
                this.actualTrendData = [];
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    plotComponents() {
        if (this.activeSubTab === GraphType.TREND) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.activeMetric &&
                this.activeMetric.coachedComponents &&
                this.activeMetric.coachedComponents.length
            ) {
                let timer = 0;
                this.loadedTrend = false;
                this.activeMetric.coachedComponents.forEach((c, idx) => {
                    timer += 800;
                    this.selectedComponents.push(c.metricId.toString());

                    this._ngZone.runOutsideAngular(() => {
                        this._ngZone.run(() => {
                            setTimeout(() => {
                                this.onComponentSelected({
                                    value: c.metricId.toString(),
                                });

                                if (
                                    idx + 1 ===
                                    this.activeMetric.coachedComponents.length
                                ) {
                                    this.loadedTrend = true;
                                }
                            }, timer);
                        });
                    });
                });
            } else {
                this.loadedTrend = true;
            }
        } else {
            this.loadedTrend = true;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _emitDone(state) {
        this.triggerDone.emit(state);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleHistory(el) {
        this.showCommitmentHistory = !this.showCommitmentHistory;

        if (this.showCommitmentHistory === true && el && el.nativeElement) {
            this.scrollToExpanded(el);
        }
    }

    toggleMetric(opp: any): void {
        opp.active = true;
        this.onActiveMetricSelected(opp.metricId);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleUncoached(force) {
        this.showUncoachedFlag =
            force !== null && force !== undefined
                ? force
                : !this.showUncoachedFlag;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.extradata) {
            this.extradata.unsubscribe();
        }

        if (this.resizesub) {
            this.resizesub.unsubscribe();
        }

        this._teamOAService.setRowExpanded(false);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    newObservation() {
        this.iframe('Observation', 'new');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    newCommitment() {
        const max = Number(
            this._componentAttributes
                ? this._componentAttributes.commitmentCountMax
                : 0,
        );
        const warn = Number(
            this._componentAttributes
                ? this._componentAttributes.commitmentCountWarn
                : 0,
        );

        const statuses = [
            GoalStatus.ACTIVE,
            GoalStatus.PRE_APPROVAL,
            GoalStatus.UPDATING,
            GoalStatus.PENDING_CLOSE,
        ];

        // eslint-disable-next-line @typescript-eslint/prefer-includes
        const allcomms = this.member.allcommitments.filter(
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            (a: CommitmentListEntry) => statuses.indexOf(a.statusId) > -1,
        );

        if (max) {
            if (allcomms.length >= max) {
                alert(
                    this._translateService.instant(
                        TranslationKeyMap._7468_THIS_PERSON_ALREADY_HAS_TOO_MANY_COMMITMENTS__PLEASE_CLOSE_SOME_BEFORE_OPENING_ANOTHER,
                    ),
                );
                return;
            } else if (
                allcomms.length >= warn &&
                !confirm(
                    this._translateService.instant(
                        TranslationKeyMap._7469_THIS_PERSON_ALREADY_HAS_SEVERAL_COMMITMENTS__ARE_YOU_SURE_YOU_WANT_TO_ADD_ANOTHER_,
                    ),
                )
            ) {
                return;
            }
        }
        this.iframe('Commitment', 'new');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    iframe(eventtype: string, stateOrId: string | number) {
        this._coachingService.openIFrame(
            eventtype,
            stateOrId,
            this.member.userId,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    scrollToExpanded(el) {
        setTimeout(() => {
            // https://plainjs.com/javascript/styles/get-the-position-of-an-element-relative-to-the-document-24/
            const rect = el.getBoundingClientRect();
            const scrollLeft =
                window.pageXOffset || document.documentElement.scrollLeft;
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const offset = {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                top: rect.top + scrollTop,
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                left: rect.left + scrollLeft,
            };

            const navbarheight = document
                .querySelector('cmps-nav>.navbar.fixed-top')
                .getBoundingClientRect().height;
            const top = offset.top - navbarheight - 17;
            try {
                window.scrollTo({
                    left: 0,
                    top,
                    behavior: 'smooth',
                });
            } catch (e) {
                window.scrollTo(0, top);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    allPropsEmpty(data) {
        for (const g in data) {
            if (data[g]) {
                return false;
            }
        }
        return true;
    }
}
